import cutileiApi, { UPLOAD_TIMEOUT } from "./cutileiApi"
import AuthService from "./auth"
import { GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE, isNetworkError } from "./apiError"
import download from 'downloadjs'
import { DateTime } from 'luxon'

// O relatório vem como blob, então a mensagem de erro precisa ser lida do corpo e
// parseada — e nenhum desses passos existe quando a falha é de rede.
async function getBlobErrorMessage (error) {
  if (isNetworkError (error)) return NETWORK_ERROR_MESSAGE

  try {
    return JSON.parse (await error.response.data.text ()).message ?? GENERIC_ERROR_MESSAGE
  } catch {
    return GENERIC_ERROR_MESSAGE
  }
}

export default class ReportService {
  static REPORT_ROUTES = {
    'Avaliações de profissionais': '/reports/ratings/professional',
    'Avaliações do salão': '/reports/ratings/business',
    'Clientes aniversariantes': '/reports/customers/birthday',
    'Clientes com créditos e dívidas': '/reports/customers/credits',
    'Clientes com pacotes adquiridos': '/reports/customers/bundles',
    'Clientes duplicados': '/reports/customers/duped',
    'Clientes excluídos': '/reports/customers/deleted',
    'Clientes novos': '/reports/customers/new',
    'Clientes por serviço': '/reports/services/customers',
    'Clientes que faltaram ou cancelaram': '/reports/customers/missed',
    'Clientes que fecharam comandas com cortesia': '/reports/customers/courtesy',
    'Clientes que não retornaram': '/reports/customers/inactive',
    'Clientes que retornaram': '/reports/customers/returned',
    'Comandas excluídas': '/reports/bills/deleted',
    'Entradas e saídas de estoque': '/reports/products/stock_changes',
    'Produtos em estoque': '/reports/products/stock',
    'Profissionais que receberam caixinha': '/reports/professionals/tips',
    'Rendimentos por categoria de serviço': '/reports/earnings/service_category',
    'Rendimentos por método de pagamento': '/reports/earnings/payment_method',
    'Serviços mais vendidos': '/reports/services/sold',
    'Suprimentos e sangrias': '/reports/earnings_expenses',
    'Todos os clientes': '/reports/customers'
  }

  static buildQuery (filters) {
    const params = []
    const businessId = AuthService.getBusinessId ()

    if (businessId) {
      params.push (`business_id=${businessId}`)
    }

    if (filters) {
      if (Array.isArray (filters.visit_period) && filters.visit_period.length === 2) {
        params.push (`visit_start_date=${encodeURIComponent (filters.visit_period[0])}`)
        params.push (`visit_end_date=${encodeURIComponent (filters.visit_period[1])}`)
      }

      if (Array.isArray (filters.check_period) && filters.check_period.length === 2) {
        params.push (`check_start_date=${encodeURIComponent (filters.check_period[0])}`)
        params.push (`check_end_date=${encodeURIComponent (filters.check_period[1])}`)
      }

      if (Array.isArray (filters.period) && filters.period.length === 2) {
        params.push (`start_date=${encodeURIComponent (filters.period[0])}`)
        params.push (`end_date=${encodeURIComponent (filters.period[1])}`)
      }

      if (filters.status && typeof filters.status === 'object' && filters.status.value) {
        params.push (`status=${encodeURIComponent (filters.status.value)}`)
      }

      if (filters.service && Array.isArray (filters.service)) {
        params.push (`service_ids=${filters.service.map (s => s.value).join (',')}`)
      }
    }

    return params.length ? '?' + params.join ('&') : ''
  }

  static buildReportFileName (reportName, filters) {
    let dateRange = ''

    const formatDate = dateStr => {
      if (!dateStr) return ''
      const d = DateTime.fromISO (dateStr)
      if (!d.isValid) return ''
      return d.toFormat ('dd-MM-yyyy')
    }

    if (filters) {
      if (Array.isArray (filters.visit_period) && filters.visit_period.length === 2) {
        dateRange = `${formatDate (filters.visit_period[0])} a ${formatDate (filters.visit_period[1])}`
      } else if (Array.isArray (filters.check_period) && filters.check_period.length === 2) {
        dateRange = `${formatDate (filters.check_period[0])} a ${formatDate (filters.check_period[1])}`
      } else if (Array.isArray (filters.period) && filters.period.length === 2) {
        dateRange = `${formatDate (filters.period[0])} a ${formatDate (filters.period[1])}`
      }
    }

    let status = ''
    let serviceName = ''

    if (filters && filters.status && typeof filters.status === 'object') {
      status = filters.status.label
    }

    if (filters && filters.service && Array.isArray (filters.service)) {
      for (const service of filters.service) {
        if (service.label) serviceName += ` - ${service.label}`
      }
    }

    let fileName = reportName
    if (status) fileName += ` - ${status}`
    if (serviceName) fileName += serviceName
    if (dateRange) fileName += ` (${dateRange})`
    fileName += '.xlsx'

    return fileName
  }

  static async handleDownload (filters, reportName, setDownloading) {
    setDownloading (true)
    try {
      const token = AuthService.getToken ()
      const requestConfig = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
      
      const route = this.REPORT_ROUTES[reportName]

      if (!route) {
        alert ('Relatório não implementado!')
        return
      }

      const query = this.buildQuery (filters)
      const url = `${route}${query}`
      const { data: reportData } = await cutileiApi.get (
        url, { responseType: 'blob', timeout: UPLOAD_TIMEOUT, ...requestConfig }
      )

      download (reportData, this.buildReportFileName (reportName, filters))
    } catch (error) {
      throw new Error (await getBlobErrorMessage (error))
    } finally {
      setDownloading (false)
    }
  }
}
